import type { ReactNode } from 'react';
import { Anchor, AppShell, Box, Button, Container, Group, NativeSelect, Text } from '@mantine/core';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { SectionNav, useNarrowLayout, type Section } from './SectionNav';
import { VerifyEmailBanner } from './VerifyEmailBanner';
import { ColourSchemeToggle } from './ColourSchemeToggle';

const HEADER_HEIGHT = 64;
const RAIL_WIDTH = 224;

/**
 * The three places the dashboard goes.
 *
 * Everything that is not making an interaction or reading an email list lives under Settings, which
 * has a list of its own. Three is the number somebody can hold in their head while thinking about
 * something else, and the something else is always the article they are writing.
 */
const SECTIONS: readonly Section[] = [
  { to: '/interactions', label: 'Interactions', icon: <InteractionsIcon /> },
  { to: '/newsletters', label: 'Email lists', icon: <EmailListIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

/**
 * The frame around every signed in page: who you are, which publisher, where to go, and a way out.
 *
 * A stable sidebar holds the publisher and navigation; narrow screens use the same links in a row.
 */
export function Layout() {
  const { user, org, orgs, selectOrg, signOut } = useAuth();
  const narrow = useNarrowLayout();

  const location = useLocation();
  const currentSection =
    SECTIONS.find((section) => location.pathname.startsWith(section.to))?.label ?? 'Workspace';
  const initials =
    user?.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('') ?? 'Q';

  return (
    <AppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{ width: RAIL_WIDTH, breakpoint: 'md', collapsed: { mobile: true, desktop: narrow } }}
    >
      <a href="#workspace-main" className="skip-link">
        Skip to content
      </a>
      <AppShell.Header className="workspace-header">
        <Group h="100%" px={narrow ? 'md' : 'lg'} gap="md" wrap="nowrap" justify="space-between">
          <Group gap="xl" wrap="nowrap">
            <Anchor
              component={Link}
              to="/interactions"
              className="workspace-wordmark"
              underline="never"
              c="inherit"
            >
              <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <rect width="32" height="32" rx="8" fill="#c94327" />
                <path
                  d="M8 10h12M8 16h16M8 22h8"
                  stroke="#fffefa"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              quizee
            </Anchor>
            <Text className="workspace-breadcrumb" size="sm" c="dimmed" visibleFrom="md">
              Workspace <span aria-hidden="true">/</span> <strong>{currentSection}</strong>
            </Text>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <ColourSchemeToggle />
            <Button variant="subtle" color="gray" size="compact-sm" onClick={() => void signOut()}>
              Sign out
            </Button>
            <span className="workspace-avatar" aria-label={user?.name}>
              {initials}
            </span>
          </Group>
        </Group>
      </AppShell.Header>
      {!narrow && (
        <AppShell.Navbar className="workspace-sidebar" p="md">
          {orgs.length > 1 ? (
            <NativeSelect
              aria-label="Publisher"
              value={org?.org_id ?? ''}
              onChange={(event) => selectOrg(event.target.value)}
              data={orgs.map((candidate) => ({ value: candidate.org_id, label: candidate.name }))}
            />
          ) : (
            <div className="publisher-card">
              <span className="publisher-monogram">{org?.name.slice(0, 1) ?? 'Q'}</span>
              <div>
                <strong>{org?.name ?? 'Your workspace'}</strong>
                <span>Editorial workspace</span>
              </div>
            </div>
          )}
          <Text className="rail-label" mt={28} mb="sm" ml="sm">
            WORKSPACE
          </Text>
          <SectionNav label="Sections" sections={SECTIONS} orientation="vertical" />
          <div className="rail-bottom">
            <div className="rail-person">
              <span className="workspace-avatar">{initials}</span>
              <div>
                <strong>{user?.name}</strong>
                <span>{org?.role ?? 'Team member'}</span>
              </div>
            </div>
          </div>
        </AppShell.Navbar>
      )}
      <AppShell.Main>
        <Container size={1200} className="workspace-content">
          {narrow && (
            <>
              {orgs.length > 1 ? (
                <NativeSelect
                  mb="sm"
                  aria-label="Publisher"
                  value={org?.org_id ?? ''}
                  onChange={(event) => selectOrg(event.target.value)}
                  data={orgs.map((candidate) => ({
                    value: candidate.org_id,
                    label: candidate.name,
                  }))}
                />
              ) : (
                <Text size="xs" c="dimmed" mb="sm">
                  {org?.name}
                </Text>
              )}
              <SectionNav label="Sections" sections={SECTIONS} orientation="horizontal" />
            </>
          )}
          <Box id="workspace-main" tabIndex={-1} style={{ minWidth: 0 }}>
            <VerifyEmailBanner />
            <Outlet />
          </Box>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}

/** The shared drawing of every navigation icon: line art, no fill, sized to a label. */
function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function InteractionsIcon() {
  return (
    <Glyph>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-5 4Z" />
      <path d="M8.5 8.5h7M8.5 12h4" />
    </Glyph>
  );
}

function EmailListIcon() {
  return (
    <Glyph>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 7.4 5.3a2 2 0 0 0 2.2 0L20.5 7" />
    </Glyph>
  );
}

function SettingsIcon() {
  return (
    <Glyph>
      <path d="M4 7h6M14 7h6M4 17h10M18 17h2" />
      <circle cx="12" cy="7" r="2" />
      <circle cx="16" cy="17" r="2" />
    </Glyph>
  );
}
